// /src/components/CartModal.jsx
import React from "react";
import { useTranslation } from 'react-i18next';
import API_BASE_URL from "../api";
import { authFetch } from "../authFetch";
import { useAuth } from "../authContext";

// --- Trust lists & URL guards ---
const TRUSTED_PAYMENT_ROOTS = ["monobank.ua"]; // оплата
const TRUSTED_OWN_ROOTS = ["radiomonitor.net"]; // возврат после оплаты

const _hostMatches = (host, roots) =>
  roots.some((root) => host === root || host.endsWith("." + root));

const isTrustedPaymentUrl = (u) => {
  try {
    const { protocol, hostname } = new URL(u);
    return protocol === "https:" && _hostMatches(hostname, TRUSTED_PAYMENT_ROOTS);
  } catch {
    return false;
  }
};

const isTrustedReturnUrl = (u) => {
  try {
    const { protocol, hostname } = new URL(u);
    return protocol === "https:" && _hostMatches(hostname, TRUSTED_OWN_ROOTS);
  } catch {
    return false;
  }
};

const navigateIfTrusted = (u, t) => {
  if (isTrustedPaymentUrl(u) || isTrustedReturnUrl(u)) {
    window.location.href = u; // допускаем только https и доверенные хосты (включая поддомены)
    return true;
  }
  if (t) alert(t('cart.untrusted_payment_link'));
  return false;
};

const safeHttpHref = (u) => {
  // для отображения stream_url: разрешаем только http/https, режем javascript:/data:
  try {
    const { protocol } = new URL(u);
    return protocol === "https:" || protocol === "http:" ? u : null;
  } catch {
    return null;
  }
};

export default function CartModal({
  open,
  items = [],
  onRemove = () => {},
  onClose = () => {},
  onCheckout = () => {},
}) {
  const { isAuth } = useAuth();
  const { t } = useTranslation('common');
  const [submitting, setSubmitting] = React.useState(false);
  const modalRef = React.useRef(null);

  // === A11y: фокус в модалке, Esc для закрытия, Tab-трап ===
  React.useEffect(() => {
    if (!open) return;
    const root = modalRef.current;
    if (!root) return;

    const prevActive = document.activeElement;

    const focusables = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));

    const list = focusables();
    if (list.length) list[0].focus();
    else {
      root.setAttribute("tabindex", "-1");
      root.focus();
    }

    const onKey = (e) => {
      if (e.key === "Escape") {
        try {
          typeof onClose === "function" && onClose();
        } catch {}
      } else if (e.key === "Tab") {
        const els = focusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (active === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prevActive && typeof prevActive.focus === "function") {
        prevActive.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const total = (items || []).reduce(
    (sum, it) => sum + (Number(it.meta?.price_uah) || 0),
    0
  );

  // === Название позиции как в инвойсе Monobank ===
  const formatEntity = (preferred, fallbackId, fallbackKey) => {
    if (preferred) return preferred;
    const safeId = fallbackId ?? "?";
    return t(fallbackKey, { id: safeId });
  };

  const basketName = (it) => {
    const p = (it.product_code || "").toUpperCase();
    const meta = it.meta || {};
    if (p === "STATS") {
      const radio = formatEntity(meta.radio_name, meta.radio_id, "cart.basket.radio_id");
      return t("cart.basket.stats", { radio });
    }
    if (p === "MONITORING") {
      const name = formatEntity(meta.stream_name, it.stream_id, "cart.basket.stream_id");
      return t("cart.basket.monitoring", { name });
    }
    return t("cart.basket.default");
  };

  // === Checkout через Monobank ===
  async function handleCheckout() {
    if (submitting) return; // защита от двойного клика
    if (!isAuth) {
      alert(t('auth.need_login'));
      return;
    }
    if (!items || items.length === 0) {
      alert(t('cart.empty'));
      return;
    }
    try {
      setSubmitting(true);
      const API = (API_BASE_URL || "").replace(/\/+$/, "");
      const res = await authFetch(API + "/cart/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = (data && (data.detail || data.message)) || t('cart.checkout_error');
        alert(typeof msg === "string" ? msg : JSON.stringify(msg));
        return;
      }

      // разные варианты поля с URL от Monobank:
      const url =
        data?.pageUrl ||
        data?.page_url ||
        data?.invoice?.pageUrl ||
        data?.invoice?.page_url;

      if (url) {
        try {
          onClose?.();
        } catch (e) {}
        navigateIfTrusted(url, t);
      } else {
        alert(t('cart.invoice_missing_link'));
      }
    } catch (e) {
      alert(t('common.network_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="cart-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="cart-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-modal-title"
        aria-describedby="cart-modal-body"
        ref={modalRef}
        style={{
          width: "min(92vw, 440px)",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 12px 32px rgba(0,0,0,.25)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          style={{
            background: "#00723a",
            color: "#fff",
            padding: "16px 22px",
            fontWeight: 800,
            fontSize: 20,
            letterSpacing: ".3px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span id="cart-modal-title">{t('cart.title')}</span>
          <button
            onClick={onClose}
            type="button"
            aria-label={t('cart.close_aria')}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 28,
              cursor: "pointer",
              marginRight: -8,
              marginTop: -4,
              fontWeight: 400,
            }}
            title={t('common.close')}
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div id="cart-modal-body" style={{ padding: "20px 22px 22px" }}>
          {!items || items.length === 0 ? (
            <div style={{ color: "#888", fontSize: 17, textAlign: "center", minHeight: 80 }}>
              {t('cart.empty')}
            </div>
          ) : (
            <>
              <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
                {items.map((item) => (
                  <li
                    key={item.id}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "14px 0 9px 0",
                      display: "block",
                    }}
                  >
                    {/* Заголовок позиции (как в банк-инвойсе) */}
                    <div style={{ fontWeight: 400, fontSize: 18 }}>
                      {basketName(item)}
                    </div>

                    {/* Для STATS ссылку на stream_url не показываем */}
                    {!(item.product_code === "STATS") && (
                      <div style={{ fontSize: 14, color: "#0a8d56", marginBottom: 4 }}>
                        {(() => {
                          const href = safeHttpHref(item.meta?.stream_url);
                          return href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#00723a", textDecoration: "underline" }}
                            >
                              {href}
                            </a>
                          ) : (
                            <span style={{ color: "#a00" }} title={t('cart.unsafe_link_blocked')}>
                              {String(item.meta?.stream_url || "")}
                            </span>
                          );
                        })()}
                      </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 15 }}>
                      <span>
                        <b>{Number(item.meta.price_uah || 0).toFixed(2)} ₴</b>
                        {item.unit_price_eur && (
                          <span style={{ color: "#888", fontSize: 13, marginLeft: 4 }}>
                            (€{item.unit_price_eur})
                          </span>
                        )}
                      </span>
                      <span style={{ color: "#888" }}>
                        {item.product_code === "STATS" ? "" : (item.duration_days ? t('cart.for_days', { days: item.duration_days }) : "")}
                      </span>
                      <button
                        className="cart-item__remove"
                        onClick={() => onRemove(item.id)}
                        aria-label={`${t('cart.remove')}: ${
                          item.product_code === "STATS" && item?.meta?.radio_name
                            ? t('cart.stats_access_short', { radio: item.meta.radio_name })
                            : (item.meta?.stream_name || item.title || item.name || item.product_code || t('cart.item'))
                        }`}
                        style={{
                          marginLeft: "auto",
                          background: "#fff",
                          border: "1px solid #e54848",
                          color: "#e54848",
                          padding: "4px 12px",
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        {t('cart.remove')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  marginTop: 16,
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="cart-total"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {t('cart.total')}: {total.toFixed(2)} ₴
                </div>
                <button
                  onClick={onCheckout || handleCheckout}
                  disabled={submitting}
                  style={{
                    padding: "10px 18px",
                    fontSize: 16,
                    borderRadius: 8,
                    border: "none",
                    background: submitting ? "#6aa58a" : "#00723a",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: submitting ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.8 : 1,
                  }}
                >
                  {submitting ? t('cart.creating_invoice') : t('cart.pay')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
